import { z } from 'zod';
import { objectIdSchema, isValidObjectIdSchema } from '../../utils/index.js';
import { baseSchema, baseFESchema } from '../base.domain.js';
import { ErrorTypes } from '../../utils/constants.js';
import { mcqStructureSchema } from './structure/mcq.structure.js';
import { msqStructureSchema } from './structure/msq.structure.js';
import { blankStructureSchema } from './structure/blank.structure.js';
import { openStructureSchema } from './structure/open.structure.js';
import { drawStructureSchema } from './structure/draw.structure.js';
import { audioStructureSchema } from './structure/audio.structure.js';
import { videoStructureSchema } from './structure/video.structure.js';
import { matchStructureSchema } from './structure/match.structure.js';
import { reorderStructureSchema } from './structure/reorder.structure.js';
import { dragAndDropStructureSchema } from './structure/draganddrop.structure.js';
import { dropDownStructureSchema } from './structure/dropdown.structure.js';
import { dndImageStructureSchema } from './structure/dndimage.structure.js';
import { hotspotStructureSchema } from './structure/hotspot.structure.js';
import { classificationStructureSchema } from './structure/classification.structure.js';
import { equationStructureSchema } from './structure/equation.structure.js';
import { graphingStructureSchema } from './structure/graphing.structure.js';
import { QuestionType } from './structure/base.structure.js';
import { comprehensionStructureSchema } from './structure/comprehension.structure.js';
import { wordcloudStructureSchema } from './structure/wordcloud.structure.js';
import { slideV2StructureSchema } from './structure/slidev2.structure.js';

const MAXIMUM_MARKS = 1;
const MINIMUM_MARKS = 0;
const defaultMarks = {
  maximum: MAXIMUM_MARKS,
  minimum: MINIMUM_MARKS
};
const baseQuestionSchema = baseSchema.extend({
  time: z.number(),
  startedAt: z.string().optional(),
  ver: z.number().default(2).optional(),
  clones: z.array(objectIdSchema).optional(),
  parent: z.nullable(objectIdSchema).default(null).optional(),
  index: z.number().min(-1).optional(),
  comprehensionQuestionIds: z.array(objectIdSchema).optional(),
  comprehensionQuestionId: z.nullable(objectIdSchema).default(null).optional(),
  comprehensionTimestamp: z.number().int().min(0).optional(),
  cloned: z.boolean().default(false).optional().nullable(),
  published: z.boolean().optional(),
  standards: z.array(z.string()).optional(),
  topics: z.array(z.string()).optional(),
  standardIds: z.array(z.unknown()).optional(),
  isSuperParent: z.boolean().nullable().default(false).optional(),
  teleportFrom: z.nullable(z.string()).default(null).optional(),
  cloneFrom: z.string().optional(),
  aiGenerated: z.boolean().optional(),
  aiCreateMeta: z.object({
    hasDiagram: z.boolean().optional(),
    sourceImage: z.string().optional(),
    imgSuggestionCancelled: z.boolean().optional(),
    isAddedFromRecommendations: z.boolean().optional(),
    parent: z.string().optional()
  }).optional().nullable(),
  quizId: z.string().optional(),
  numClones: z.number().optional()
});
const baseQuestionFESchema = baseFESchema.merge(baseQuestionSchema.extend({
  clones: z.array(isValidObjectIdSchema).optional(),
  parent: z.nullable(isValidObjectIdSchema).default(null).optional(),
  standards: z.array(isValidObjectIdSchema).optional(),
  standardIds: z.array(z.unknown()).optional()
}));
const questionSchema = z.discriminatedUnion("type", [
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.MCQ),
    comprehensionQuestionId: z.string().optional(),
    structure: mcqStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.MSQ),
    comprehensionQuestionId: z.string().optional(),
    structure: msqStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.BLANK),
    comprehensionQuestionId: z.string().optional(),
    structure: blankStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.OPEN),
    comprehensionQuestionId: z.string().optional(),
    structure: openStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.WORDCLOUD),
    comprehensionQuestionId: z.string().optional(),
    structure: wordcloudStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.DRAW),
    structure: drawStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.AUDIO),
    structure: audioStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.VIDEO),
    structure: videoStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.MATCH),
    structure: matchStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.REORDER),
    structure: reorderStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.DRAGNDROP),
    structure: dragAndDropStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.DROPDOWN),
    structure: dropDownStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.EQUATION),
    structure: equationStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.DND_IMAGE),
    structure: dndImageStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.HOTSPOT),
    structure: hotspotStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.GRAPHING),
    structure: graphingStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.CLASSIFICATION),
    structure: classificationStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.COMPREHENSION),
    comprehensionQuestionIds: z.array(z.string()).min(1, {
      message: ErrorTypes.MIN_QUESTIONS
    }),
    structure: comprehensionStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.COMPREHENSION_VIDEO),
    comprehensionQuestionIds: z.array(z.string()).min(1, {
      message: ErrorTypes.MIN_QUESTIONS
    }),
    structure: comprehensionStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.SLIDEV2),
    structure: slideV2StructureSchema
  })
]);
const questionFESchema = z.discriminatedUnion("type", [
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.MCQ),
    structure: mcqStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.MSQ),
    structure: msqStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.BLANK),
    structure: blankStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.OPEN),
    structure: openStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.WORDCLOUD),
    structure: wordcloudStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.DRAW),
    structure: drawStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.AUDIO),
    structure: audioStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.VIDEO),
    structure: videoStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.MATCH),
    structure: matchStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.REORDER),
    structure: reorderStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.DRAGNDROP),
    structure: dragAndDropStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.DROPDOWN),
    structure: dropDownStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.EQUATION),
    structure: equationStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.DND_IMAGE),
    structure: dndImageStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.HOTSPOT),
    structure: hotspotStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.GRAPHING),
    structure: graphingStructureSchema
  }),
  baseQuestionFESchema.extend({
    type: z.literal(QuestionType.CLASSIFICATION),
    structure: classificationStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.COMPREHENSION),
    comprehensionQuestionIds: z.array(z.string()).min(1, {
      message: ErrorTypes.MIN_QUESTIONS
    }),
    structure: comprehensionStructureSchema
  }),
  baseQuestionSchema.extend({
    type: z.literal(QuestionType.COMPREHENSION_VIDEO),
    comprehensionQuestionIds: z.array(z.string()).min(1, {
      message: ErrorTypes.MIN_QUESTIONS
    }),
    structure: comprehensionStructureSchema
  })
]);

export { MAXIMUM_MARKS, MINIMUM_MARKS, defaultMarks, questionFESchema, questionSchema };
